import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { gameAPI } from '../services/api'
import {
  CompareGame,
  TIME_PERIODS,
  DEFAULT_MAX_GUESSES,
  DEFAULT_SCOREBOARD_SIZE,
  NewsSource,
  ComparePreset,
  COMPARE_PRESETS,
  TimePeriod,
} from '../types'
import { Box, Button, Container, Grid, Paper, Stack, Typography, Chip, Divider, Alert } from '@mui/material'
import { PlayArrow as PlayIcon, CompareArrows as CompareIcon } from '@mui/icons-material'
import LoadingSpinner from '../components/LoadingSpinner'
import AdvancedSettings from './components/AdvancedSettings'
import SourceCard from './components/SourceCard'
import AvailableSourceCard from './components/AvailableSourceCard'
import ComparePresetsDialog from './components/ComparePresetsDialog'

/**
 * This is the selection page for the Source Comparison game mode.
 * It allows the user to select two groups of sources and start the game.
 */
const Compare: React.FC = () => {
  const { user, isLoading } = useAuth0()

  const navigate = useNavigate()

  // State variables for the page & game settings
  const [loading, setLoading] = useState(false)
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriod>(TIME_PERIODS.PAST_WEEK)
  const [maxGuesses, setMaxGuesses] = useState(DEFAULT_MAX_GUESSES)
  const [scoreboardSize, setScoreboardSize] = useState(DEFAULT_SCOREBOARD_SIZE)
  const [unlimitedGuesses, setUnlimitedGuesses] = useState(false)
  const [presetDialogOpen, setPresetDialogOpen] = useState(false)

  // State variables for the two groups of sources
  const [groupA, setGroupA] = useState<NewsSource[]>([])
  const [groupB, setGroupB] = useState<NewsSource[]>([])

  // Available sources (not in either group)
  const allSources = Object.values(NewsSource)
  const availableSources = allSources.filter(source => !groupA.includes(source) && !groupB.includes(source))

  const handleAddToGroupA = (source: NewsSource) => {
    setGroupA(prev => [...prev, source])
  }

  const handleAddToGroupB = (source: NewsSource) => {
    setGroupB(prev => [...prev, source])
  }

  const handleRemoveFromGroupA = (source: NewsSource) => {
    setGroupA(prev => prev.filter(s => s !== source))
  }

  const handleRemoveFromGroupB = (source: NewsSource) => {
    setGroupB(prev => prev.filter(s => s !== source))
  }

  const handleMoveToGroupA = (source: NewsSource) => {
    setGroupB(prev => prev.filter(s => s !== source))
    setGroupA(prev => [...prev, source])
  }

  const handleMoveToGroupB = (source: NewsSource) => {
    setGroupA(prev => prev.filter(s => s !== source))
    setGroupB(prev => [...prev, source])
  }

  const handleApplyPreset = (preset: ComparePreset) => {
    setGroupA(preset.groupASources)
    setGroupB(preset.groupBSources)
  }

  // Function to start the game
  const handleStartGame = async () => {
    // Check if at least one source is in each group
    if (groupA.length === 0 || groupB.length === 0) {
      alert('Please add at least one source to each group before starting.')
      return
    }

    setLoading(true)
    try {
      // Create the game object with proper settings
      const game: CompareGame = {
        score: 0,
        created_at: new Date().toISOString(),
        max_guesses: unlimitedGuesses ? -1 : maxGuesses,
        scoreboard_size: scoreboardSize,
        time_period: selectedTimePeriod,
        sources_group_a: groupA,
        sources_group_b: groupB,
        guessed_words_group_a: [],
        guessed_words_group_b: [],
        remaining_guesses: unlimitedGuesses ? -1 : maxGuesses,
        is_completed: false,
        user_id: user?.sub,
      }

      const { data, error } = await gameAPI.createComparativeGame(game)

      if (error) {
        console.error('Error creating game:', error)
        return
      }

      const gameId = data.id
      // Navigate to compare game
      navigate(`/compare/${gameId}`)
    } catch (error) {
      console.error('Failed to create game:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CompareIcon sx={{ fontSize: 60, mb: 2, color: 'primary.main' }} />
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Source Comparison Mode
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Compare word usage between two groups of news sources. Organize sources into Group A and Group B to see how
          their language differs.
        </Typography>
      </Box>

      {/* Instructions Alert */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>How it works:</strong> Choose a preset or click the <strong style={{ color: '#1976d2' }}>blue</strong>{' '}
          or <strong style={{ color: '#dc004e' }}>red</strong> plus button on available sources to add them to{' '}
          <strong style={{ color: '#1976d2' }}>Group A</strong> or <strong style={{ color: '#dc004e' }}>Group B</strong>
          . You can also move sources between groups using the swap icon, or remove them using the delete icon. You need
          at least one source in each group to start.
        </Typography>
      </Alert>

      {/* Two Groups Layout */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Group A */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              height: '100%',
              minHeight: 400,
              border: '3px solid',
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                Group A
              </Typography>
              <Chip label={`${groupA.length} source${groupA.length !== 1 ? 's' : ''}`} color="primary" size="small" />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {groupA.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 300,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  bgcolor: 'white',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No sources yet. Click on available sources below to add them here.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {groupA.map(source => (
                  <SourceCard
                    key={source}
                    source={source}
                    onRemove={() => handleRemoveFromGroupA(source)}
                    onMoveToOther={() => handleMoveToGroupB(source)}
                    groupLabel="Group A"
                    groupColor="#1976d2"
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Group B */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              height: '100%',
              minHeight: 400,
              border: '3px solid',
              borderColor: 'secondary.main',
              bgcolor: 'secondary.50',
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5" fontWeight="bold" color="secondary.main">
                Group B
              </Typography>
              <Chip label={`${groupB.length} source${groupB.length !== 1 ? 's' : ''}`} color="secondary" size="small" />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {groupB.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 300,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  bgcolor: 'white',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No sources yet. Click on available sources below to add them here.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {groupB.map(source => (
                  <SourceCard
                    key={source}
                    source={source}
                    onRemove={() => handleRemoveFromGroupB(source)}
                    onMoveToOther={() => handleMoveToGroupA(source)}
                    groupLabel="Group B"
                    groupColor="#dc004e"
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Available Sources */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 'bold' }}>
            Available Sources
          </Typography>
          <Button variant="outlined" onClick={() => setPresetDialogOpen(true)}>
            Browse Presets
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click the <strong style={{ color: '#1976d2' }}>blue</strong> or{' '}
          <strong style={{ color: '#dc004e' }}>red</strong> plus button to add a source to{' '}
          <strong style={{ color: '#1976d2' }}>Group A</strong> or <strong style={{ color: '#dc004e' }}>Group B</strong>
          .
        </Typography>
        {availableSources.length === 0 ? (
          <Alert severity="success">All sources have been assigned to groups!</Alert>
        ) : (
          <Grid container spacing={2}>
            {availableSources.map(source => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={source}>
                <AvailableSourceCard
                  source={source}
                  handleAddToGroupA={handleAddToGroupA}
                  handleAddToGroupB={handleAddToGroupB}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Advanced Settings */}
      <AdvancedSettings
        selectedTimePeriod={selectedTimePeriod}
        setSelectedTimePeriod={setSelectedTimePeriod}
        maxGuesses={maxGuesses}
        setMaxGuesses={setMaxGuesses}
        scoreboardSize={scoreboardSize}
        setScoreboardSize={setScoreboardSize}
        handleStartGame={handleStartGame}
        loading={loading}
        isLoading={isLoading}
        showSources={false}
        showStartButton={false}
        unlimitedGuesses={unlimitedGuesses}
        setUnlimitedGuesses={setUnlimitedGuesses}
      />

      {/* Start Game Button */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleStartGame}
          disabled={loading || isLoading || groupA.length === 0 || groupB.length === 0}
          startIcon={loading ? <LoadingSpinner size="sm" /> : <PlayIcon />}
          sx={{
            px: 6,
            py: 2,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #1976D2 0%, #DC024E 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1870C9 0%, #CA0248 100%)',
            },
            '&:disabled': {
              background: 'grey.300',
            },
          }}
        >
          {loading ? 'Creating Game...' : isLoading ? 'Loading...' : 'Start Comparison Game'}
        </Button>
        {(groupA.length === 0 || groupB.length === 0) && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Please add at least one source to each group to start
          </Typography>
        )}
      </Box>
      <ComparePresetsDialog
        open={presetDialogOpen}
        onClose={() => setPresetDialogOpen(false)}
        presets={COMPARE_PRESETS}
        onApplyPreset={handleApplyPreset}
      />
    </Container>
  )
}

export default Compare
